import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtMoney0, fmtPct, fmtDate, statutLabel, statutColor } from "../lib/format";
import { Modal, Field, Badge, Spinner, EmptyState } from "../components/ui";

function Kpi({ label, value, alert, testId }: { label: string; value: string; alert?: boolean; testId: string }) {
  return (
    <div data-testid={testId} className="nx-card nx-fade-up p-4">
      <p className="text-[11px] font-bold uppercase tracking-wide text-muted">{label}</p>
      <p className={`mt-1 text-xl font-extrabold ${alert ? "text-danger" : "text-green"}`}>{value}</p>
    </div>
  );
}

const TRANCHES = ["J-0", "J-30", "J-60", "J-90", "J-120"];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<any>(null);
  const [entites, setEntites] = useState<any[]>([]);
  const [relance, setRelance] = useState<any>(null);
  const [encaisse, setEncaisse] = useState<any>(null);
  const [form, setForm] = useState<any>({});
  const [err, setErr] = useState<string | null>(null);

  const load = () => api.get("/dashboard").then((r) => setData(r.data));
  useEffect(() => {
    load();
    api.get("/entites").then((r) => setEntites(r.data));
  }, []);

  if (!data) return <Spinner />;
  const k = data.kpi;
  const mgr = user?.role === "manager";

  const doRelance = async () => {
    try {
      await api.post(`/devis/${relance.id}/relances`, form);
      setRelance(null);
      setForm({});
      load();
    } catch (e) {
      setErr(apiError(e));
    }
  };

  const doEncaisse = async () => {
    try {
      await api.post(`/devis/${encaisse.id}/encaissement`, form);
      setEncaisse(null);
      setForm({});
      load();
    } catch (e) {
      setErr(apiError(e));
    }
  };

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-green">Dashboard</h1>
        <p className="text-sm text-muted">Devise de reporting : {k.devise_reporting}</p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {mgr ? (
          <>
            <Kpi testId="kpi-ca" label="CA réalisé (payé)" value={fmtMoney0(k.ca_realise)} />
            <Kpi testId="kpi-marge" label={`Marge réalisée${k.marge_pct != null ? ` (${k.marge_pct} %)` : ""}`} value={fmtMoney0(k.marge_realisee)} />
            <Kpi testId="kpi-encaisser" label="Gagné à encaisser" value={fmtMoney0(k.gagne_a_encaisser)} />
            <Kpi testId="kpi-pipeline" label="Pipeline pondéré" value={fmtMoney0(k.pipeline_pondere)} />
            <Kpi testId="kpi-recurrent" label="Revenu récurrent" value={`${fmtMoney0(k.revenu_recurrent)} · 50 % : ${fmtMoney0(k.revenu_recurrent_pondere)}`} />
            <Kpi testId="kpi-renouv" label="Taux de renouvellement" value={fmtPct(k.taux_renouvellement)} alert={k.taux_renouvellement != null && k.taux_renouvellement < 60} />
            <Kpi testId="kpi-closing" label="Taux de closing" value={fmtPct(k.taux_closing)} alert={k.taux_closing != null && k.taux_closing < 50} />
            <Kpi testId="kpi-cycle" label="Cycle de vente moyen" value={k.cycle_vente_jours != null ? `${k.cycle_vente_jours} j` : "—"} />
            <Kpi testId="kpi-frs-reglees" label="Factures frs réglées" value={fmtMoney0(k.frs_reglees)} />
            <Kpi testId="kpi-frs-non-reglees" label="Factures frs non réglées" value={fmtMoney0(k.frs_non_reglees)} />
          </>
        ) : (
          <>
            <Kpi testId="kpi-opportunites" label="Opportunités en cours" value={String(k.nb_opportunites)} />
            <Kpi testId="kpi-renouvellements" label="Renouvellements à traiter" value={String(k.nb_renouvellements)} />
            <Kpi testId="kpi-tickets" label="Tickets ouverts" value={String(k.nb_tickets_ouverts)} />
            <Kpi testId="kpi-leads" label="Leads actifs" value={String(k.nb_leads_actifs)} />
          </>
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <section className="nx-card p-4" data-testid="bloc-opportunites">
          <h2 className="mb-3 text-base font-bold text-green">Opportunités en cours</h2>
          {data.opportunites.length === 0 ? (
            <EmptyState message="Aucune opportunité en cours" />
          ) : (
            <div className="max-h-72 overflow-y-auto">
              <table className="nx-table">
                <thead><tr><th>N°</th><th>Objet</th><th>Client</th><th>Statut</th>{mgr && <th className="text-right">Montant HT</th>}</tr></thead>
                <tbody>
                  {data.opportunites.map((o: any) => (
                    <tr key={o.id}>
                      <td><Link className="font-bold text-green underline-offset-2 hover:underline" to={`/devis/${o.id}`}>{o.numero}</Link></td>
                      <td className="max-w-[220px] truncate">{o.objet}</td>
                      <td>{o.client_nom}</td>
                      <td><Badge className={statutColor(o.statut)}>{statutLabel(o.statut)} · {o.probabilite} %</Badge></td>
                      {mgr && <td className="text-right font-semibold">{fmtMoney0(o.total_ht, o.devise)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="nx-card p-4" data-testid="bloc-renouvellements">
          <h2 className="mb-3 text-base font-bold text-green">Renouvellements par tranche</h2>
          <div className="mb-3 flex gap-2">
            {TRANCHES.map((t) => {
              const nb = data.renouvellements.filter((r: any) => r.tranche === t).length;
              return (
                <div key={t} className={`flex-1 rounded-lg border px-2 py-1.5 text-center ${nb ? "border-lime bg-lime-soft" : "border-line bg-white"}`}>
                  <p className="text-[11px] font-bold text-muted">{t}</p>
                  <p className="text-lg font-extrabold text-green">{nb}</p>
                </div>
              );
            })}
          </div>
          {data.renouvellements.length === 0 ? (
            <EmptyState message="Aucun renouvellement sous 120 jours" />
          ) : (
            <div className="max-h-48 overflow-y-auto">
              <table className="nx-table">
                <thead><tr><th>Réf.</th><th>Client</th><th>Échéance</th><th>Tranche</th>{mgr && <th className="text-right">Montant</th>}</tr></thead>
                <tbody>
                  {data.renouvellements.map((r: any) => (
                    <tr key={r.id}>
                      <td className="font-semibold">{r.reference}</td>
                      <td>{r.client_nom}</td>
                      <td>{fmtDate(r.date_echeance)}</td>
                      <td><Badge className={r.tranche === "J-0" ? "bg-red-50 text-danger" : "bg-soft text-green"}>{r.tranche}</Badge></td>
                      {mgr && <td className="text-right">{fmtMoney0(r.montant)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="nx-card p-4" data-testid="bloc-tickets">
          <h2 className="mb-3 text-base font-bold text-green">Tickets ouverts</h2>
          {data.tickets_ouverts.length === 0 ? (
            <EmptyState message="Aucun ticket ouvert" />
          ) : (
            <div className="max-h-72 overflow-y-auto">
              <table className="nx-table">
                <thead><tr><th>Réf.</th><th>Objet</th><th>Priorité</th><th>Client</th><th>Assigné</th></tr></thead>
                <tbody>
                  {data.tickets_ouverts.map((t: any) => (
                    <tr key={t.id}>
                      <td><Link className="font-bold text-green underline-offset-2 hover:underline" to={`/tickets?ticket=${t.id}`}>{t.reference}</Link></td>
                      <td className="max-w-[220px] truncate">{t.objet}</td>
                      <td>{t.priorite && <Badge className={t.priorite === "P1" ? "bg-red-50 text-danger" : "bg-soft text-green"}>{t.priorite}</Badge>}</td>
                      <td>{t.client_nom}</td>
                      <td>{t.assigne_nom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="nx-card p-4" data-testid="bloc-relances">
          <h2 className="mb-3 text-base font-bold text-green">Devis à relancer</h2>
          {data.devis_a_relancer.length === 0 ? (
            <EmptyState message="Aucun devis à relancer" />
          ) : (
            <div className="max-h-72 overflow-y-auto">
              <table className="nx-table">
                <thead><tr><th>N°</th><th>Client</th><th>Statut</th><th>Âge</th><th className="text-right">Actions</th></tr></thead>
                <tbody>
                  {data.devis_a_relancer.map((d: any) => (
                    <tr key={d.id}>
                      <td><Link className="font-bold text-green underline-offset-2 hover:underline" to={`/devis/${d.id}`}>{d.numero}</Link></td>
                      <td>{d.client_nom}</td>
                      <td><Badge className={statutColor(d.statut)}>{statutLabel(d.statut)}</Badge></td>
                      <td>{d.age_jours} j</td>
                      <td className="text-right">
                        <div className="flex justify-end gap-1.5">
                          <button data-testid={`relance-btn-${d.numero}`} className="nx-btn-ghost !px-2 !py-1 text-xs" onClick={() => { setForm({}); setRelance(d); }}>
                            Relance
                          </button>
                          {mgr && (
                            <button data-testid={`encaissement-btn-${d.numero}`} className="nx-btn-lime !px-2 !py-1 text-xs" onClick={() => { setForm({}); setEncaisse(d); }}>
                              Encaissement
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {relance && (
        <Modal title={`Relance du devis ${relance.numero}`} onClose={() => setRelance(null)} testId="relance-modal">
          <div className="space-y-3">
            <Field label="Date">
              <input data-testid="relance-date-input" type="date" className="nx-input" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </Field>
            <Field label="Contact">
              <input data-testid="relance-contact-input" className="nx-input" value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Personne contactée" />
            </Field>
            <Field label="Note">
              <textarea data-testid="relance-note-input" className="nx-input" rows={3} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Objet de la relance, engagement pris…" />
            </Field>
            {err && <p className="text-sm text-danger">{err}</p>}
            <div className="flex justify-end gap-2">
              <button className="nx-btn-ghost" onClick={() => setRelance(null)}>Annuler</button>
              <button data-testid="relance-save-btn" className="nx-btn-primary" onClick={doRelance}>Enregistrer</button>
            </div>
          </div>
        </Modal>
      )}

      {encaisse && (
        <Modal title={`Encaissement confirmé — devis ${encaisse.numero}`} onClose={() => setEncaisse(null)} testId="encaissement-modal">
          <div className="space-y-3">
            <Field label="Entité de facturation">
              <select data-testid="encaissement-entite-select" className="nx-input" value={form.entite_id || ""} onChange={(e) => setForm({ ...form, entite_id: e.target.value })}>
                <option value="">— Choisir —</option>
                {entites.map((e) => (
                  <option key={e.id} value={e.id}>{e.code} — {e.nom}</option>
                ))}
              </select>
            </Field>
            <Field label="Date d'encaissement prévue">
              <input data-testid="encaissement-date-input" type="date" className="nx-input" value={form.date_encaissement || ""} onChange={(e) => setForm({ ...form, date_encaissement: e.target.value })} />
            </Field>
            <Field label="Note">
              <textarea data-testid="encaissement-note-input" className="nx-input" rows={2} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Référence BC, mode de paiement…" />
            </Field>
            {err && <p className="text-sm text-danger">{err}</p>}
            <div className="flex justify-end gap-2">
              <button className="nx-btn-ghost" onClick={() => setEncaisse(null)}>Annuler</button>
              <button data-testid="encaissement-save-btn" className="nx-btn-primary" disabled={!form.entite_id} onClick={doEncaisse}>
                Confirmer l'encaissement
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
